import { useCallback, useEffect, useMemo, useState } from 'react';
import { getJanelasDescarga } from '@spark/services/descarga';
import { useLatestAsyncRequest } from '@spark/ui-components/hooks/useLatestAsyncRequest';

const FILTROS_INICIAIS = { Busca: '' };

export const useJanelasListagem = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [refreshKey, setRefreshKey] = useState(0);
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  const { beginRequest, isCurrentRequest, isMounted } = useLatestAsyncRequest();

  const fetchData = useCallback(async () => {
    if (!isMounted()) return;

    const requestId = beginRequest();

    try {
      setLoading(true);

      const params = {
        PaginaAtual: paginationState.pageIndex + 1,
        QuantidadePorPagina: paginationState.pageSize
      };
      const busca = String(filtros.Busca ?? '').trim();
      if (busca) params.Busca = busca;

      const response = await getJanelasDescarga(params);
      if (!isCurrentRequest(requestId)) return;

      const responseData = response?.data || response || {};
      const registros = Array.isArray(responseData.registros) ? responseData.registros : [];

      setData(registros);
      setTotalRegistros(responseData.totalRegistros || registros.length);
    } catch (error) {
      if (!isCurrentRequest(requestId)) return;
      console.error('Erro ao buscar janelas de descarga:', error);
    } finally {
      if (isCurrentRequest(requestId)) {
        setLoading(false);
      }
    }
    // refreshKey força nova busca quando os filtros são reaplicados com o mesmo valor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beginRequest, filtros, isCurrentRequest, isMounted, paginationState.pageIndex, paginationState.pageSize, refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterApply = useCallback((novosFiltros = {}) => {
    setFiltros({ ...FILTROS_INICIAIS, ...novosFiltros });
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handlePaginationChange = useCallback((next) => {
    setLoading(true);
    setPaginationState((prev) => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev, pageIndex: next };
      if (next && typeof next === 'object') return { ...prev, ...next };
      return prev;
    });
  }, []);

  return useMemo(() => ({
    data,
    loading,
    totalRegistros,
    filtros,
    paginationState,
    handleFilterApply,
    handlePaginationChange,
    fetchData
  }), [data, loading, totalRegistros, filtros, paginationState, handleFilterApply, handlePaginationChange, fetchData]);
};

export default useJanelasListagem;
